import { Spacer } from "@chakra-ui/layout";
import { Box, Flex, Text } from "@familyzone/component-library";
import React, { useEffect, useState } from "react";
import { Link } from "react-router";
import AmountField, { getAmount } from "../../../modules/AmountField";
import Api from "../../../utils/Api";
import CardSpinner from "./CardSpinner";
import { topTrafficTypesCardSorter } from "./TopTrafficTypesCardHelpers";
import { ApiDates } from "../../../utils/DateTimeUtil";

interface ITopTrafficTypesCard {
  user: string;
  dateFilters: ApiDates;
}

const TopTrafficTypesCard = ({ user, dateFilters }: ITopTrafficTypesCard): JSX.Element => {
  const [data, setData] = useState<TopTrafficTypesCardItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    prepareTrafficData(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, dateFilters]);

  const prepareTrafficData = (user: string) => {
    setLoading(true);
    Api.get_analytics(
      "/surfwize/analytics/traffic/typesandwebsites?filter_user=" + user,
      (data: TopTrafficCardAnalyticsApiCallResponse) => {
        const sortedData = topTrafficTypesCardSorter(data);
        setData(sortedData);
        setLoading(false);
      },
      (err: unknown) => {
        // How do we want to handle API errors on the front-end?
        console.error("An error occurred fetching series data from the API. Error reported:", err);
        setLoading(false);
      },
      { dateOverride: dateFilters }
    );
  };

  return (
    <Box background="neutrals.0" paddingX="sp16" paddingY="sp24" marginBottom="sp16" borderRadius="sm">
      <Flex marginBottom="sp12">
        <Text fontFamily="heading" fontWeight="500" fontSize="16px" color="text.title" as="h2">
          Top Traffic Types
        </Text>
        <Spacer />
        <Link to={"/surfwize/reporting/types?filter_user=" + user} aria-label="Top Traffic Types. View All">
          <Text color="brand.500" fontSize="sm">
            View all
          </Text>
        </Link>
      </Flex>
      {loading && <CardSpinner />}
      {!loading &&
        data.map((item, index) => {
          const amount = item.upload + item.download;
          return (
            <Box key={item.name}>
              <Flex p="sp8" background={index % 2 === 0 ? "neutrals.20" : "neutrals.0"}>
                <Link to={itemLink(item) + user} aria-label={`${item.name}: ${getAmount(amount)}`}>
                  <Text color="brand.500" fontSize="md">
                    {item.name}
                  </Text>
                </Link>
                <Spacer />
                <Text>
                  <AmountField amount={amount} />
                </Text>
              </Flex>
            </Box>
          );
        })}
    </Box>
  );
};

export default TopTrafficTypesCard;

const itemLink = (item: TopTrafficTypesCardItem): string => {
  return item.signature ? `/surfwize/reporting/type/${item.tag}/user/` : `/surfwize/reporting/website/${item.website}/user/`;
};

export type TopTrafficTypesCardItem = {
  all_domains: string[];
  categoryId: string;
  "categoryId.string": string;
  day: string;
  domain: string;
  download: number;
  hits: number;
  name: string;
  signature: boolean;
  subCategoryId: string;
  "subCategoryId.string": string;
  tag: string;
  "tag.string": string;
  transfer: number;
  upload: number;
  website: string;
};

export type TopTrafficCardAnalyticsApiCallResponse = {
  data: {
    upload: number;
    download: number;
    hits: number;
    transfer: number;
    domain: string;
    website: string;
    categoryId: string;
    subCategoryId: string;
    tag: string;
    day: string;
    all_domains?: string[];
    "categoryId.string"?: string;
    "subCategoryId.string"?: string;
    "tag.string"?: string;
    name?: string;
    signature?: boolean;
  }[];
};
